/**
 * Public Profile Service
 * 
 * Manages public user profiles that can be read by anyone.
 * This is separate from /users which is private.
 * 
 * Collection: /publicProfiles/{userId}
 */

import { doc, setDoc, updateDoc, getDoc, collection, query, where, getDocs, onSnapshot, orderBy, limit } from 'firebase/firestore';
import { db } from '../firebaseConfig';
import { User } from '../types';

export interface PublicProfile {
  id: string;
  userId: string;
  name: string;
  avatar: string;
  level: number;
  xp: number;
  title?: string;
  badges?: string[];
  username?: string; // Optional username for search/display
  referralCode?: string; // Optional referral code for validation (public for referral system)
  isPublic: boolean;
  updatedAt: Date;
}

/**
 * Sync user data to public profile
 * Call this whenever user data changes (name, avatar, level, xp, title, badges)
 */
export const syncToPublicProfile = async (userId: string, userData: Partial<User>): Promise<void> => {
  try {
    const publicProfileRef = doc(db, 'publicProfiles', userId);
    
    const publicProfileData: Partial<PublicProfile> = {
      userId,
      name: userData.name || '',
      avatar: userData.avatar || userData.photoURL || `https://api.dicebear.com/7.x/avataaars/svg?seed=${userId}`,
      level: userData.level || 1,
      xp: userData.xp || 0,
      title: userData.title,
      badges: userData.badges || [],
      username: userData.username, // Include username if available
      referralCode: userData.referralStats?.referralCode, // Include referralCode if available
      isPublic: userData.public !== false, // Default to true unless explicitly false
      updatedAt: new Date()
    };
    
    // Remove undefined fields
    Object.keys(publicProfileData).forEach(key => {
      if (publicProfileData[key as keyof PublicProfile] === undefined) {
        delete publicProfileData[key as keyof PublicProfile];
      }
    });
    
    // Use setDoc with merge to create or update
    await setDoc(publicProfileRef, publicProfileData, { merge: true });
    
    if (import.meta.env.DEV) {
      console.log('[PUBLIC_PROFILE] ✅ Synced to public profile:', userId);
    }
  } catch (error: any) {
    // Log but don't throw - this is a non-critical sync operation
    if (import.meta.env.DEV) {
      console.error('[PUBLIC_PROFILE] ❌ Error syncing to public profile:', {
        userId,
        code: error?.code,
        message: error?.message
      });
    }
  }
};

/**
 * Get public profile by userId
 */
export const getPublicProfile = async (userId: string): Promise<PublicProfile | null> => {
  try {
    const publicProfileRef = doc(db, 'publicProfiles', userId);
    const snapshot = await getDoc(publicProfileRef);
    
    if (snapshot.exists()) {
      return { id: snapshot.id, ...snapshot.data() } as PublicProfile;
    }
    
    return null;
  } catch (error: any) {
    if (import.meta.env.DEV) {
      console.error('[PUBLIC_PROFILE] Error getting public profile:', {
        userId,
        code: error?.code,
        message: error?.message
      });
    }
    return null;
  }
};

/**
 * Search public profiles by name
 */
export const searchPublicProfiles = async (searchTerm: string, excludeUserId?: string): Promise<PublicProfile[]> => {
  try {
    const searchLower = searchTerm.toLowerCase().trim();
    
    if (searchLower.length < 2) {
      return [];
    }
    
    // Query public profiles by name (case-insensitive search requires client-side filtering)
    const publicProfilesRef = collection(db, 'publicProfiles');
    const snapshot = await getDocs(publicProfilesRef);
    
    const profiles = snapshot.docs
      .map(doc => ({ id: doc.id, ...doc.data() } as PublicProfile))
      .filter(profile => {
        // Filter by search term (client-side)
        const nameMatch = profile.name?.toLowerCase().includes(searchLower);
        
        // Exclude specified userId
        if (excludeUserId && profile.userId === excludeUserId) {
          return false;
        }
        
        // Only include public profiles
        if (profile.isPublic !== true) {
          return false;
        }
        
        return nameMatch;
      })
      .slice(0, 20); // Limit results
    
    return profiles;
  } catch (error: any) {
    if (import.meta.env.DEV) {
      console.error('[PUBLIC_PROFILE] Error searching public profiles:', {
        searchTerm,
        code: error?.code,
        message: error?.message
      });
    }
    return [];
  }
};

/**
 * Get multiple public profiles by userIds
 */
export const getPublicProfilesByIds = async (userIds: string[]): Promise<PublicProfile[]> => {
  try {
    if (userIds.length === 0) {
      return [];
    }
    
    const profiles: PublicProfile[] = [];
    
    // Firestore 'in' query supports max 10 items, so batch if needed
    const batchSize = 10;
    for (let i = 0; i < userIds.length; i += batchSize) {
      const batch = userIds.slice(i, i + batchSize);
      const publicProfilesRef = collection(db, 'publicProfiles');
      const q = query(publicProfilesRef, where('userId', 'in', batch));
      const snapshot = await getDocs(q);
      
      snapshot.docs.forEach(doc => {
        const data = doc.data();
        if (data.isPublic !== false) {
          profiles.push({ id: doc.id, ...data } as PublicProfile);
        }
      });
    }
    
    return profiles;
  } catch (error: any) {
    if (import.meta.env.DEV) {
      console.error('[PUBLIC_PROFILE] Error getting public profiles by IDs:', {
        userIds,
        code: error?.code,
        message: error?.message
      });
    }
    return [];
  }
};

